import { create } from 'zustand';

interface ITogglePlayPauseOptions {
    // Force HTML5 Audio for this play so it streams and starts at canplay instead of
    // downloading + decoding the whole file first (Web Audio, the default). Only the
    // VoicesV2 preview opts into this; every other listing keeps the default behaviour.
    html5?: boolean
}

interface IPreGenAudioStore {
    src: string | null
    // Stable per-card/row key so the same voice in two different lists doesn't both show as active.
    activeKey: string | null
    isPlaying: boolean

    loadingSrc: string | null
    // Whether the currently loaded src should play via HTML5 Audio (streaming) rather
    // than Web Audio. Set per-play by togglePlayPause and read by PreGenAudioPlayer.
    html5: boolean
    togglePlayPause: (src: string, key: string, options?: ITogglePlayPauseOptions) => void
    stop: () => void
    clearLoading: () => void
}

export const usePreGenAudioPlayer = create<IPreGenAudioStore>()((set) => ({
    src: null,
    activeKey: null,
    isPlaying: false,
    loadingSrc: null,
    html5: false,
    togglePlayPause: (src: string, key: string, options?: ITogglePlayPauseOptions) => set((state) => {
        if (state.activeKey === key) {
            const willPlay = !state.isPlaying;
            return { isPlaying: willPlay, loadingSrc: willPlay ? src : null };
        }
        // Same src genuinely already playing (not just requested) under a different key.
        if (state.src === src && state.isPlaying && !state.loadingSrc) {
            return { activeKey: key, isPlaying: true, loadingSrc: null };
        }
        return {
            src,
            activeKey: key,
            isPlaying: true,
            loadingSrc: src,
            html5: !!options?.html5,
        }
    }),
    stop: () => set(() => ({
        src: null,
        activeKey: null,
        isPlaying: false,
        loadingSrc: null,
        html5: false,
    })),
    clearLoading: () => set(() => ({ loadingSrc: null })),
}))
